/**
 * Plan change flow.
 *
 * Two payment routes, shown according to what the server says is actually
 * available:
 *   - Pay by card  → Stripe Checkout (only when the server reports stripeEnabled
 *                    and the plan has a configured price).
 *   - Pay cash     → records a request and emails the admin, who activates it.
 *
 * The card button is never rendered when checkout cannot work, so there is no
 * button that looks live but fails.
 */
import React, { useState } from "react";
import { CreditCard, Banknote, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { useApi, API_ENDPOINTS } from "../../lib/api";
import { Plan, formatPrice, planBullets } from "../../config/plans";
import { Subscription } from "../../types/subscription";

interface PlanChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: Plan | null;
  subscription: Subscription | null;
}

const PlanChangeDialog: React.FC<PlanChangeDialogProps> = ({
  open,
  onOpenChange,
  plan,
  subscription,
}) => {
  const api = useApi();
  const [busy, setBusy] = useState<"card" | "cash" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);

  if (!plan) return null;

  const price = formatPrice(plan);
  const canPayByCard =
    !!subscription?.stripeEnabled && subscription.purchasablePlans.includes(plan.id);

  const payByCard = async () => {
    setBusy("card");
    setError(null);
    try {
      const res = await api.post(API_ENDPOINTS.SUBSCRIPTION_CHECKOUT, { plan: plan.id });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError("Could not start checkout. Please try again.");
    } catch (err: any) {
      setError(err.response?.data?.message || "Could not start checkout. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const requestPlan = async () => {
    setBusy("cash");
    setError(null);
    try {
      await api.post(API_ENDPOINTS.SUBSCRIPTION_REQUEST, { plan: plan.id });
      setRequested(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Could not send your request. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {requested ? "Request received" : `Switch to ${plan.name}`}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 pt-0 space-y-5">
          {requested ? (
            <>
              <p className="text-sm text-gray-600">
                Thanks — we've recorded your request for <strong>{plan.name}</strong> and
                notified the team. We'll contact you shortly to arrange payment, and your
                plan will be activated as soon as it's received.
              </p>
              <Button className="w-full" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </>
          ) : (
            <>
              <div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-3xl font-bold">{price.amount}</span>
                  {price.suffix && (
                    <span className="text-gray-500 text-sm">{price.suffix}</span>
                  )}
                </div>
                <ul className="space-y-1.5">
                  {planBullets(plan)
                    .slice(0, 3)
                    .map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-gray-700">
                        <Check className="h-4 w-4 text-green-600 shrink-0" />
                        {b}
                      </li>
                    ))}
                </ul>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {error}
                </p>
              )}

              <div className="space-y-2">
                {canPayByCard && (
                  <Button className="w-full" onClick={payByCard} disabled={busy !== null}>
                    {busy === "card" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    Pay by card
                  </Button>
                )}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={requestPlan}
                  disabled={busy !== null}
                >
                  {busy === "cash" ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Banknote className="h-4 w-4 mr-2" />
                  )}
                  Pay cash — request this plan
                </Button>
              </div>

              <p className="text-xs text-gray-500">
                {canPayByCard
                  ? "Card payments are handled securely by Stripe. Choosing cash sends us a request and we'll be in touch to arrange payment."
                  : "We'll contact you to arrange payment and activate your plan."}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanChangeDialog;
